var _a;
/**
 * MemoryCache.ts
 * @author Diao Zheng
 * @file A simple in-memory cache that stores computed objects and somesuch.
 * @barrel export createMemoryCache
 */
import LRUCache from "lru-cache"; // @deno rewrite
import { Disposable } from "./Disposable";
import * as Hashing from "./Hashing";
import * as Option from "./Option";
import * as Integer from "./Integer";
/**
 * The number of items to cache
 */
const CACHE_SIZE = 4000;
/**
 * The default time to expire (in milliseconds)
 */
const CACHE_DEFAULT_EXPIRY = 60000;
class MemoryCacheInternal {
    constructor(maxEntries) {
        this[_a] = new Disposable(this.clear.bind(this));
        this.cache = new LRUCache({
            max: maxEntries,
            maxAge: CACHE_DEFAULT_EXPIRY,
            dispose(_k, cacheLine) {
                Disposable.tryDispose(cacheLine.value);
            },
        });
    }
    get currentSize() {
        return this.cache.itemCount;
    }
    /**
     * Perform a set operation with a precomputed hash. Doing this will NOT
     * perform any collision avoidance.
     * @param key the precomputed hash
     * @param value the data to cache
     * @param expiry if present, the epoch (in ms) that the data will expire.
     * Defaults to 60 seconds from now.
     */
    UNSAFE_set(key, value, expiry) {
        this.setInternal(key, key, value, this.mapExpiryToAge(expiry));
    }
    /**
     * Updates the cache key with the said value. Note that this is dependent
     * on hash not colliding (which is unlikely).
     * @param key the key to store
     * @param value the data to cache
     * @param expiry if present, the epoch (in ms) that the data will expire.
     * Defaults to 60 seconds from now.
     */
    set(key, value, expiry) {
        this.setInternal(Hashing.hash(key), key, value, this.mapExpiryToAge(expiry));
    }
    /**
     * Returns the current cache line keyed by the precomputed hash. This will
     * not perform collision avoidance, but will remove the cache line should the
     * cache expires.
     * @param key the precomputed hash
     */
    UNSAFE_getLine(key) {
        return this.cache.get(key);
    }
    UNSAFE_get(key) {
        const data = this.UNSAFE_getLine(key);
        if (data && data.key === key) {
            return data.value;
        }
        return;
    }
    get(key, comparator) {
        const defaultHash = Hashing.hash(key);
        const defaultData = this.UNSAFE_getLine(defaultHash);
        if (defaultData) {
            if (comparator) {
                if (comparator(defaultData.key, key)) {
                    return defaultData.value;
                }
            }
            else if (defaultData.key === key) {
                return defaultData.value;
            }
        }
        return;
    }
    clear() {
        this.cache.reset();
    }
    UNSAFE_printAllCacheValues() {
        let output = "";
        this.cache.forEach((data, key) => {
            output += (`\nkey: ${key} (0x${Integer.toHexUnsafe(key)})\tvalue: ` +
                JSON.stringify(data, undefined, 2) + "\n");
        });
        // eslint-disable-next-line no-console
        console.warn(output);
    }
    constructCacheLine(key, value) {
        return { key, value };
    }
    mapExpiryToAge(expiry) {
        switch (typeof expiry) {
            case "object":
                return expiry.age;
            default:
                return Option.map(expiry, (n) => n - Date.now());
        }
    }
    setInternal(hash, key, value, age) {
        this.cache.set(hash, this.constructCacheLine(key, value), Option.mapFinite(age));
    }
}
_a = Disposable.Instance;
export const MemoryCache = new MemoryCacheInternal(CACHE_SIZE);
export function createMemoryCache(cacheSize) {
    return new MemoryCacheInternal(Option.value(cacheSize, CACHE_SIZE));
}
